import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { X, MapPin, Heart, ExternalLink, Navigation } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { HolidayContentView } from './view/HolidayContentView';
import { HolidayValue, getHolidayOption } from '@/constants/holidays';

interface Display {
  id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  holiday_type: string;
  display_type: string;
  image_url: string;
  featured: boolean;
  upvote_count: number;
  holidays?: string[]; // New: array of holidays this display celebrates
}

interface DisplayDetailDrawerTabbedProps {
  display: Display;
  onClose: () => void;
  className?: string;
}

/**
 * DisplayDetailDrawerTabbed - Enhanced drawer with multi-holiday support
 *
 * Shows holiday-specific content in tabs when display celebrates multiple holidays.
 * Smart default selection based on current date.
 */
export const DisplayDetailDrawerTabbed: React.FC<DisplayDetailDrawerTabbedProps> = ({
  display,
  onClose,
  className = ""
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('');

  // Determine smart default holiday tab based on current date
  useEffect(() => {
    if (!display.holidays || display.holidays.length === 0) {
      setActiveTab('default');
      return;
    }

    const getDefaultHoliday = () => {
      const today = new Date();
      const month = today.getMonth(); // 0-11

      // October: Halloween
      if (month === 9 && display.holidays!.includes('Halloween')) return 'Halloween';
      // Nov-Dec: Christmas
      if ([10, 11].includes(month) && display.holidays!.includes('Christmas')) return 'Christmas';
      // Feb: Valentine's Day
      if (month === 1 && display.holidays!.some(h => h.includes('Valentine'))) {
        return display.holidays!.find(h => h.includes('Valentine')) || display.holidays![0];
      }
      // July: 4th of July
      if (month === 6 && display.holidays!.some(h => h.includes('July'))) {
        return display.holidays!.find(h => h.includes('July')) || display.holidays![0];
      }
      // Easter (March-April)
      if ([2, 3].includes(month) && display.holidays!.includes('Easter')) return 'Easter';

      // Default to first holiday
      return display.holidays![0];
    };

    setActiveTab(getDefaultHoliday());
  }, [display.holidays]);

  const getLocationString = () => {
    const parts = [display.city, display.state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Location not specified';
  };

  const handleViewDetails = () => {
    navigate(`/display/${display.id}`);
  };

  const handleGetDirections = () => {
    const url = `https://maps.google.com/maps?daddr=${display.latitude},${display.longitude}`;
    window.open(url, '_blank');
  };

  const renderHolidayTab = (holiday: string) => {
    const holidayOption = getHolidayOption(holiday);
    const icon = holidayOption?.icon || '✨';
    const label = holidayOption?.label || holiday;

    return (
      <TabsTrigger key={holiday} value={holiday} className="flex items-center gap-1 text-xs">
        <span>{icon}</span>
        <span className="hidden sm:inline">{label}</span>
      </TabsTrigger>
    );
  };

  const holidays = display.holidays || [];
  const hasMultipleHolidays = holidays.length > 1;

  return (
    <div className={`bg-background border-r border-border h-full flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold text-foreground">Display Details</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Top Section: Image, Name, Location (Always Visible) */}
        <div className="p-4 space-y-4 border-b">
          {/* Image */}
          {display.image_url && (
            <div className="relative">
              <img
                src={display.image_url}
                alt={display.name}
                className="w-full h-48 object-cover rounded-lg border border-border"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div
                className="w-full h-48 bg-muted rounded-lg flex items-center justify-center border border-border"
                style={{ display: 'none' }}
              >
                <MapPin className="w-8 h-8 text-muted-foreground" />
              </div>
              {display.featured && (
                <Badge className="absolute top-2 right-2 bg-yellow-500 text-yellow-50">
                  ⭐ Featured
                </Badge>
              )}
            </div>
          )}

          {/* Title and Location */}
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-foreground leading-tight">
              {display.name}
            </h3>

            <div className="flex items-center gap-1 text-muted-foreground">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">{getLocationString()}</span>
            </div>
          </div>

          {/* Tags */}
          <div className="flex items-center gap-2 flex-wrap">
            {display.display_type && (
              <Badge variant="outline" className="text-xs">
                {display.display_type}
              </Badge>
            )}
            {display.upvote_count > 0 && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Heart className="w-4 h-4" />
                {display.upvote_count}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <Button
              onClick={handleViewDetails}
              className="w-full"
              size="sm"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Full Details
            </Button>

            <Button
              onClick={handleGetDirections}
              variant="outline"
              className="w-full"
              size="sm"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Get Directions
            </Button>
          </div>
        </div>

        {/* Holiday Content Section */}
        {hasMultipleHolidays && activeTab ? (
          <div className="p-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${holidays.length}, minmax(0, 1fr))` }}>
                {holidays.map(renderHolidayTab)}
              </TabsList>

              {holidays.map((holiday) => (
                <TabsContent key={holiday} value={holiday} className="mt-4">
                  <HolidayContentView
                    displayId={parseInt(display.id)}
                    holiday={holiday as HolidayValue}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </div>
        ) : holidays.length === 1 ? (
          /* Single Holiday - No Tabs */
          <div className="p-4">
            <HolidayContentView
              displayId={parseInt(display.id)}
              holiday={holidays[0] as HolidayValue}
            />
          </div>
        ) : (
          /* Fallback: Legacy display with no holidays array */
          <div className="p-4">
            {display.description && (
              <Card className="p-3">
                <p className="text-sm text-foreground leading-relaxed">
                  {display.description}
                </p>
              </Card>
            )}
          </div>
        )}

        {/* Coordinates (for debugging/reference) */}
        <div className="text-xs text-muted-foreground px-4 pb-4">
          <p>Coordinates: {display.latitude.toFixed(6)}, {display.longitude.toFixed(6)}</p>
        </div>
      </div>
    </div>
  );
};

export default DisplayDetailDrawerTabbed;
